import { privatize } from '@/lib/utils/revenueUtils'
import { cn } from '@/lib/utils'
import { fmtMoney } from './attributionView'
import type { CollectedSplit } from './collectedSplit'

/**
 * "Where the $X collected in Stripe came from" (Paper "Attribution · New vs
 * renewal revenue", under the chart): one stacked bar, each payment counted
 * once. New purchases from ad clicks are solid revenue color; renewals credited
 * to an earlier campaign are an outlined tint (not new return on ads); other
 * visits and unmatched payments are neutral. Renewals appear only once the
 * payment data says which payments renew.
 */

type Part = { key: string; label: string; value: number; swatch: string; bar: string }

export default function CollectedSplitCard({
  split,
  provider = 'Stripe',
  privacyMode = false,
}: {
  split: CollectedSplit
  provider?: string
  privacyMode?: boolean
}) {
  if (!(split.collected > 0)) return null
  const money = (v: number) => privatize(fmtMoney(v), privacyMode)

  const parts: Part[] = [
    {
      key: 'ads',
      label: split.renewals == null ? 'From ad clicks' : 'New purchases from ad clicks',
      value: split.fromAds,
      swatch: 'bg-chart-revenue',
      bar: 'bg-chart-revenue',
    },
    ...(split.renewals != null
      ? [{
          key: 'renewals',
          label: 'Renewals from earlier campaigns',
          value: split.renewals,
          swatch: 'bg-primary-tint ring-1 ring-inset ring-chart-2',
          bar: 'bg-primary-tint ring-1 ring-inset ring-chart-2',
        }]
      : []),
    { key: 'other', label: 'Other visits', value: split.otherVisits, swatch: 'bg-foreground/20', bar: 'bg-foreground/20' },
    { key: 'unmatched', label: 'Not matched to a visit', value: split.notMatched, swatch: 'bg-foreground/[0.08]', bar: 'bg-foreground/[0.08]' },
  ].filter((p) => p.value > 0)

  return (
    <section className="surface-raised flex flex-col gap-3 px-5 pt-4 pb-5" aria-labelledby="collected-split-title">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 id="collected-split-title" className="text-[13px] font-medium leading-4 text-foreground">
          Where the {money(split.collected)} collected in {provider} came from
        </h3>
        <span className="text-xs leading-4 text-muted-foreground">Each payment counted once, on its payment date</span>
      </div>
      <div className="flex h-2.5 gap-0.5 overflow-hidden rounded-full" role="img" aria-label={parts.map((p) => `${p.label} ${money(p.value)}`).join(', ')}>
        {parts.map((p) => (
          <div key={p.key} className={cn('h-2.5', p.bar)} style={{ width: `${(p.value / split.collected) * 100}%` }} />
        ))}
      </div>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {parts.map((p) => (
          <div key={p.key} className="flex flex-col gap-0.5">
            <dt className="flex items-center gap-1.5 text-xs leading-4 text-muted-foreground">
              <span className={cn('size-2 shrink-0 rounded-[2px]', p.swatch)} aria-hidden />
              {p.label}
            </dt>
            <dd className="text-lg font-semibold leading-6 text-foreground tabular-nums">{money(p.value)}</dd>
          </div>
        ))}
      </dl>
      {split.renewals != null && split.renewals > 0 && (
        <p className="text-xs leading-4 text-muted-foreground">
          Renewals are subscription payments that started before this range. They are not new return on ads.
        </p>
      )}
    </section>
  )
}
